import { useState } from "react";
import { SupportedLanguages } from "@repo/database";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { SettingsHeader } from "~/components/layout/settings-header";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  useForm,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import LanguagePicker from "~/components/ui/language-picker";
import { ModalInput } from "~/components/ui/modal-input";
import { MultiSelect } from "~/components/ui/multi-select";
import { useAuth } from "~/hooks/use-auth";
import { usePlatform } from "~/hooks/use-platform";
import { useUser } from "~/hooks/use-user";
import { useUserManager } from "~/managers/use-user-manager";
import { usePreferencesStore } from "~/stores/use-preferences-store";

export const Route = createFileRoute("/_main/settings/_layout/")({
  component: Settings,
});

function Settings() {
  const { t } = useTranslation("common");
  const { signOut } = useAuth();
  const { user } = useUser();
  const { platform } = usePlatform();
  const {
    setFullScreen,
    fullScreen,
    homePage,
    setHomePage,
    baseStation,
    setBaseStation,
    idleTimer,
    setIdleTimer,
    screensaverTimer,
    setScreensaverTimer,
    signOutAfterAction,
    setSignOutAfterAction,
  } = usePreferencesStore();

  const [tempBaseStation, setTempBaseStation] = useState<string | undefined>(
    baseStation,
  );

  const handleSignOut = () => {
    signOut();
  };

  const handleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  const { updateUserLanguage } = useUserManager();

  const form = useForm({
    schema: z.object({
      language: z.nativeEnum(SupportedLanguages),
    }),
    defaultValues: {
      language: user.language,
    },
  });

  const preferencesForm = useForm({
    schema: z.object({
      idleTimer: z.number().min(1),
      screensaverTimer: z.number().min(1),
      signOutAfterAction: z.boolean(),
    }),
    defaultValues: {
      idleTimer: idleTimer,
      screensaverTimer: screensaverTimer,
      signOutAfterAction: signOutAfterAction,
    },
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    await updateUserLanguage(data.language);
  });

  const handleSavePreferences = preferencesForm.handleSubmit((data) => {
    setIdleTimer(data.idleTimer);
    setScreensaverTimer(data.screensaverTimer);
    setSignOutAfterAction(data.signOutAfterAction);
    toast.success(t("preferences_saved"));
  });

  const pages = [
    {
      label: "Dashboard",
      value: "/",
    },
    {
      label: "Floor plan",
      value: "/floor-plan",
    },
  ];

  return (
    <>
      <SettingsHeader title={t("general")} />
      <div className="flex flex-col gap-4">
        {(platform === "ANDROID" || platform === "IOS") && (
          <Card>
            <CardContent className="flex flex-col gap-2">
              <Label>{t("base_station_ip")}</Label>
              <Input
                value={tempBaseStation}
                onChange={(event) => {
                  setTempBaseStation(event.target.value);
                }}
              />
              <Button
                onClick={() => {
                  setBaseStation(tempBaseStation);
                }}
                variant="success"
              >
                {t("save")}
              </Button>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <Form {...form}>
              <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-2">
                <FormField
                  control={form.control}
                  name="language"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("General.language.title")}</FormLabel>

                      <FormControl>
                        <LanguagePicker
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="col-span-full">
                  <Button type="submit" variant="success">
                    {t("save")}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Form {...preferencesForm}>
              <form
                onSubmit={handleSavePreferences}
                className="flex flex-col gap-2"
              >
                <FormField
                  control={preferencesForm.control}
                  name="signOutAfterAction"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          {t("General.sign_out_after_action_title")}
                        </FormLabel>
                        <FormDescription>
                          {t("General.sign_out_after_action_description")}
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
                <div>
                  <Button type="submit" variant="success">
                    {t("save")}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="flex flex-col space-y-2">
              <div>
                <Label>{t("idle_timer")}</Label>
                <CardDescription>
                  {t("General.idle_timer_description")}
                </CardDescription>
              </div>

              <ModalInput
                type="number"
                value={idleTimer}
                onChange={setIdleTimer}
              />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex flex-col space-y-2">
              <Label>{t("screensaver_timer")}</Label>
              <ModalInput
                type="number"
                value={screensaverTimer}
                onChange={setScreensaverTimer}
              />
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="flex flex-col space-y-2">
            <div>
              <Label>{t("General.homepage_title")}</Label>
              <CardDescription>
                {t("General.homepage_description")}
              </CardDescription>
            </div>

            <MultiSelect
              maxSelect={1}
              placeholder="Select a group..."
              onValueChange={(value) => {
                setHomePage(value[0]);
              }}
              options={pages}
              defaultValue={[homePage]}
              value={[homePage]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="gap-4 flex flex-row">
            <Button onClick={handleFullScreen}>FULL SCREEN</Button>
            <Button onClick={handleSignOut} variant="destructive">
              {t("sign_out")}
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
